/**
 * EditarUbicacionDialog (rc103) — marcar/editar la ubicación GPS del contrato
 * directamente sobre el mapa (click para marcar, arrastrar el pin para
 * afinar, o "Mi ubicación" si el técnico está en el domicilio).
 *
 * Guarda vía PATCH /contratos/{id} con motivo obligatorio → queda AUDITADO
 * con diff before/after igual que cualquier edición de contrato (F17.3).
 * "Quitar ubicación" manda nulls explícitos (también auditado).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Trash2, AlertCircle } from "lucide-react";

import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LocationPicker } from "@/components/LocationPicker";
import { cn } from "@/lib/utils";
import type { ApiError } from "@/lib/api";

import { useUpdateContrato } from "./api";
import type { ContratoDetail } from "./schemas";

type LatLng = { lat: number; lng: number };

type Props = {
  contrato: ContratoDetail;
  open: boolean;
  onOpenChange: (v: boolean) => void;
};

export function EditarUbicacionDialog({ contrato, open, onOpenChange }: Props) {
  const [pos, setPos] = useState<LatLng | null>(null);
  const [referencia, setReferencia] = useState("");
  const [motivo, setMotivo] = useState("");
  const [tiles, setTiles] = useState<"street" | "satellite" | "hybrid">("street");
  const [error, setError] = useState<string | null>(null);

  const tenia = contrato.latitud != null && contrato.longitud != null;

  useEffect(() => {
    if (open) {
      setPos(tenia ? { lat: Number(contrato.latitud), lng: Number(contrato.longitud) } : null);
      setReferencia(contrato.ubicacion_referencia ?? "");
      setMotivo("");
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contrato.id]);

  const mut = useUpdateContrato(contrato.id);

  async function guardar(quitar: boolean) {
    setError(null);
    if (motivo.trim().length < 5) {
      setError("Motivo requerido (mínimo 5 caracteres) — queda en auditoría.");
      return;
    }
    if (!quitar && !pos) {
      setError("Marca la ubicación en el mapa (click o arrastra el pin).");
      return;
    }
    try {
      await mut.mutateAsync({
        motivo: motivo.trim(),
        latitud: quitar ? null : Number(pos!.lat.toFixed(7)),
        longitud: quitar ? null : Number(pos!.lng.toFixed(7)),
        ubicacion_referencia: quitar ? null : (referencia.trim() || null),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
      toast.success(quitar ? "Ubicación eliminada" : "Ubicación guardada", {
        description: "Cambio registrado en auditoría.",
      });
      onOpenChange(false);
    } catch (e) {
      setError((e as ApiError).message ?? "No se pudo guardar");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="2xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin size={16} /> Ubicación del contrato {contrato.codigo}
          </DialogTitle>
          <DialogDescription>
            Click en el mapa para marcar, arrastra el pin para afinar, o usa
            &quot;Mi ubicación&quot; si estás en el domicilio. Todo cambio queda auditado.
          </DialogDescription>
        </DialogHeader>

        {/* Toggle de capa */}
        <div className="flex items-center justify-between">
          <div className="font-mono text-[11px] text-muted-foreground">
            {pos
              ? `${pos.lat.toFixed(6)}, ${pos.lng.toFixed(6)}`
              : "Sin ubicación marcada"}
          </div>
          <div className="inline-flex rounded-md border border-border bg-card overflow-hidden">
            {(["street", "satellite", "hybrid"] as const).map((t) => (
              <button key={t} type="button" onClick={() => setTiles(t)}
                className={cn(
                  "font-mono text-[10px] uppercase tracking-wider px-2.5 py-1 transition-colors",
                  t === tiles ? "bg-electric text-white"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/40",
                )}>
                {t === "street" ? "Calle" : t === "satellite" ? "Satélite" : "Híbrido"}
              </button>
            ))}
          </div>
        </div>

        <LocationPicker value={pos} onChange={setPos} heightPx={320} tiles={tiles} />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label>Referencia</Label>
            <Input
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Pahuatlán centro, casa azul…"
              maxLength={200}
            />
          </div>
          <div>
            <Label>Motivo del cambio *</Label>
            <Input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ubicación capturada en sitio…"
            />
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-1 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
            <AlertCircle size={12} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          {tenia ? (
            <Button
              type="button" variant="ghost" size="sm"
              className="text-destructive"
              disabled={mut.isPending}
              onClick={() => guardar(true)}
            >
              <Trash2 size={13} className="mr-1" /> Quitar ubicación
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="button" size="sm"
              disabled={mut.isPending}
              onClick={() => guardar(false)}
            >
              {mut.isPending ? "Guardando…" : "Guardar ubicación"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
